import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { FiArrowLeft, FiCheck, FiZap, FiStar, FiTrendingUp } from 'react-icons/fi';
import Footer from '../components/Layout/Footer';

const PLANS = [
  {
    id: 'free', name: 'Free', credits: '1,000', price: '$0', priceNote: 'forever',
    icon: <FiZap size={24} />, color: '#64748b', popular: false, purchasable: false,
    features: ['1,000 credits on signup', '5 tone options', 'Chat history', 'Conversation memory', 'Email support']
  },
  {
    id: 'starter', name: 'Starter', credits: '50,000', price: '$5', priceNote: 'one-time',
    icon: <FiZap size={24} />, color: '#3b82f6', popular: false, purchasable: true,
    features: ['50,000 credits', '5 tone options', 'Unlimited chat history', 'Conversation memory', 'Priority support']
  },
  {
    id: 'pro', name: 'Pro', credits: '150,000', price: '$10', priceNote: 'one-time',
    icon: <FiStar size={24} />, color: '#8b5cf6', popular: true, purchasable: true,
    features: ['150,000 credits', '5 tone options', 'Unlimited chat history', 'Conversation memory', 'Priority support', 'Best value per credit']
  },
  {
    id: 'power', name: 'Power', credits: '500,000', price: '$25', priceNote: 'one-time',
    icon: <FiTrendingUp size={24} />, color: '#f59e0b', popular: false, purchasable: true,
    features: ['500,000 credits', '5 tone options', 'Unlimited chat history', 'Conversation memory', 'Priority support', 'Lowest cost per credit']
  }
];

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);

  const handlePurchase = async (planId) => {
    if (!user) {
      navigate('/signup');
      return;
    }
    setLoading(planId);
    try {
      const visitorSessionId = sessionStorage.getItem('rc_visitor_session') || '';
      const { data } = await api.post('/credits/purchase', { planId, visitorSessionId });
      window.location.href = data.url;
    } catch {
      toast.error('Failed to start checkout');
    } finally {
      setLoading(null);
    }
  };

  return (
    <div style={styles.page}>
      <nav style={styles.nav}>
        <Link to="/" style={styles.brand}>ReplyCraft</Link>
        <button onClick={() => navigate(-1)} style={styles.backBtn}><FiArrowLeft size={16} /> Back</button>
      </nav>

      <div style={styles.container}>
        <h1 style={styles.title}>Simple, Transparent Pricing</h1>
        <p style={styles.subtitle}>Buy credits once, use anytime. No subscriptions. No recurring charges.</p>

        <div style={styles.creditInfo}>
          <span style={styles.creditBadge}>1 credit = 1 word generated</span>
          <span style={styles.creditBadge}>Credits never expire</span>
          <span style={styles.creditBadge}>No subscriptions</span>
        </div>

        <div style={styles.cards} data-pricing-grid>
          {PLANS.map(plan => (
            <div key={plan.id} style={{ ...styles.card, ...(plan.popular ? styles.cardFeatured : {}) }}>
              {plan.popular && <div style={styles.popularBadge}>Best Value</div>}
              <div style={{ ...styles.planIcon, background: plan.color + '22', color: plan.color }}>{plan.icon}</div>
              <h3 style={styles.planName}>{plan.name}</h3>
              <div style={styles.price}>{plan.price}</div>
              <p style={styles.priceNote}>{plan.credits} credits &middot; {plan.priceNote}</p>
              <ul style={styles.featureList}>
                {plan.features.map(f => (
                  <li key={f} style={styles.featureItem}>
                    <FiCheck size={16} color={plan.popular ? 'var(--accent)' : 'var(--success)'} /> {f}
                  </li>
                ))}
              </ul>
              {plan.purchasable ? (
                <button
                  onClick={() => handlePurchase(plan.id)}
                  disabled={loading === plan.id}
                  style={{ ...styles.btn, ...(plan.popular ? styles.btnPro : {}) }}
                >
                  {loading === plan.id ? 'Processing...' : `Buy ${plan.name}`}
                </button>
              ) : (
                <button onClick={() => navigate(user ? '/' : '/signup')} style={styles.btn}>
                  {user ? 'Current Plan' : 'Sign Up Free'}
                </button>
              )}
            </div>
          ))}
        </div>

        <div style={styles.refund}>
          All credit purchases are final and non-refundable. We recommend trying the free 1,000 credits before purchasing.
        </div>

        <div style={styles.faq}>
          <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 24, textAlign: 'center' }}>FAQ</h2>
          {[
            { q: 'How do credits work?', a: '1 credit = 1 word generated. When you generate replies, the total word count across all reply options is deducted from your balance.' },
            { q: 'Do credits expire?', a: 'No. Credits never expire. Buy once, use whenever you need them.' },
            { q: 'Can I buy more credits later?', a: 'Yes! You can purchase additional credit packs anytime. Credits stack on top of your existing balance.' },
            { q: 'Are credits refundable?', a: 'No. All purchases are final and non-refundable. Try the free 1,000 credits first.' },
            { q: 'What payment methods do you accept?', a: 'We accept all major credit/debit cards via Stripe. Secure, encrypted payments.' },
            { q: 'How many credits does one reply use?', a: 'A typical 3-tone reply generation uses about 300-500 credits (words). So 50,000 credits gives you roughly 100-150 reply generations.' }
          ].map((item, i) => (
            <div key={i} style={styles.faqItem}>
              <h4 style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>{item.q}</h4>
              <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{item.a}</p>
            </div>
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: 'var(--bg-primary)', display: 'flex', flexDirection: 'column' },
  nav: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid var(--border)' },
  brand: { fontSize: 20, fontWeight: 700, color: 'var(--accent)', textDecoration: 'none' },
  backBtn: { display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: 14, cursor: 'pointer' },
  container: { maxWidth: 1000, margin: '0 auto', padding: '32px 20px 60px', flex: 1 },
  title: { fontSize: 'clamp(26px, 5vw, 36px)', fontWeight: 800, textAlign: 'center', marginBottom: 8 },
  subtitle: { textAlign: 'center', color: 'var(--text-secondary)', fontSize: 16, marginBottom: 24 },
  creditInfo: { display: 'flex', justifyContent: 'center', gap: 12, marginBottom: 40, flexWrap: 'wrap' },
  creditBadge: { padding: '6px 14px', background: 'var(--accent-dim)', color: 'var(--accent)', borderRadius: 20, fontSize: 13, fontWeight: 600 },
  cards: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 32 },
  card: { background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 16, padding: '28px 18px', textAlign: 'center', position: 'relative' },
  cardFeatured: { border: '2px solid var(--accent)', transform: 'scale(1.03)' },
  popularBadge: { position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', background: 'var(--accent)', color: 'white', padding: '4px 16px', borderRadius: 20, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' },
  planIcon: { width: 48, height: 48, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' },
  planName: { fontSize: 20, fontWeight: 700, marginBottom: 8 },
  price: { fontSize: 'clamp(36px, 6vw, 48px)', fontWeight: 800 },
  priceNote: { fontSize: 14, color: 'var(--text-muted)', marginBottom: 16 },
  featureList: { listStyle: 'none', margin: '20px 0', display: 'flex', flexDirection: 'column', gap: 10, textAlign: 'left', padding: 0 },
  featureItem: { display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: 'var(--text-secondary)' },
  btn: { width: '100%', padding: '12px', background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 10, color: 'var(--text-primary)', fontSize: 15, fontWeight: 600, cursor: 'pointer' },
  btnPro: { background: 'var(--accent)', border: 'none', color: 'white' },
  refund: { textAlign: 'center', padding: '16px 20px', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 10, fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic', marginBottom: 48 },
  faq: { maxWidth: 600, margin: '0 auto' },
  faqItem: { padding: '16px 0', borderBottom: '1px solid var(--border)' }
};
